import type {
  BaseEntity,
  FieldValue,
  SimpleFieldConfig,
  SimpleTableConfig,
} from "@/components/shared/UniversalCRMTable/types";

export type StatusFieldValue = FieldValue;
export type EntityWithStatus = BaseEntity & { status?: StatusFieldValue };
export type StatusOption = { value: string; label: string };

export function hasField<T extends BaseEntity>(
  item: T | null | undefined,
  fieldName: string
): item is T & Record<string, StatusFieldValue> {
  if (!item) return false;
  return Object.prototype.hasOwnProperty.call(item, fieldName);
}

/**
 * Type-safe guard for entities that expose a status field.
 * Uses hasOwnProperty to avoid false positives from prototype chains.
 */
export function hasStatusField<T extends BaseEntity>(
  item: T | null | undefined
): item is T & { status?: StatusFieldValue } {
  return hasField(item, "status");
}

/**
 * Normalizes the status field to a string for display and selection.
 */
export function getStatusValue(item: EntityWithStatus | null | undefined): string {
  if (!item) return "";
  const status = item.status;
  if (status === null || status === undefined) return "";
  return String(status);
}

export function getFieldValue(
  item: BaseEntity | null | undefined,
  fieldName: string
): string {
  if (!item) return "";
  const value = (item as Record<string, StatusFieldValue>)[fieldName];
  if (value === null || value === undefined) return "";
  return String(value);
}

export function getStatusFieldConfig<T extends BaseEntity>(
  config?: SimpleTableConfig<T>
): { fieldName: string; fieldConfig: SimpleFieldConfig } | null {
  if (!config?.fields) return null;

  const fields = config.fields;

  // Primary source of truth: required `statusFieldId` on the table config.
  // `null` is an explicit opt-out (entities without a status concept).
  if (config.statusFieldId !== undefined) {
    if (config.statusFieldId === null) return null;
    const fieldConfig = fields[config.statusFieldId];
    if (fieldConfig) {
      return { fieldName: config.statusFieldId, fieldConfig };
    }
  }

  // Back-compat fallback for legacy callers passing partial configs.
  const explicitEntry = Object.entries(fields).find(([, field]) => field.isStatusField);
  if (explicitEntry) {
    const [fieldName, fieldConfig] = explicitEntry;
    return { fieldName, fieldConfig };
  }

  const statusField = fields.status;
  if (statusField) {
    return { fieldName: "status", fieldConfig: statusField };
  }

  return null;
}

export function getStatusOptionsFromConfig<T extends BaseEntity>(
  config?: SimpleTableConfig<T>
): StatusOption[] | null {
  const statusField = getStatusFieldConfig(config);
  if (!statusField?.fieldConfig?.options) return null;
  return statusField.fieldConfig.options.map((option) => ({
    value: option.value,
    label: option.label,
  }));
}
